import type { ReportRead } from 'definitions/report-read';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';

type UseReportReadParams = {
  experimentId: number;
  /**
   * Refetch rather than reuse what is cached. The modal wants one snapshot for the whole
   * wizard: the rows are derived from stored statistics and the run's health events, and
   * refetching mid-wizard would only make them jump under the cursor. The Debrief tab is
   * opened later, possibly after the statistics have moved, so it asks for a fresh one.
   */
  refresh?: boolean;
};

/**
 * Pertento's computed read of a concluded experiment, for the end-of-test modal and the Debrief
 * tab's card. `isError` lets callers fall back rather than refuse to render: the report is
 * optional, and so is the read.
 */
export const useReportRead = ({ experimentId, refresh = false }: UseReportReadParams) => {
  const apiClient = useClient();

  const { data, isLoading, isError } = useQuery<ReportRead>({
    enabled: !!experimentId,
    queryKey: ['EXPERIMENT_REPORT_READ', experimentId],
    queryFn: () => apiClient.get<ReportRead>(`/experiments/${experimentId}/report-read`),
    staleTime: refresh ? 0 : Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return { read: data ?? null, isLoading, isError };
};
